export const STUDY_CHECKPOINT_MS = 15_000;
export const STUDY_IDLE_MS = 120_000;

export type StudySessionCheckpoint = { endedAt: string; id: string; startedAt: string };

/**
 * Why an interval closed: `idle` after two minutes without activity, `suspended` when the timer
 * stopped running (backgrounded, clock jump), `inactive` when studying stopped (left the route,
 * lost focus, unmounted).
 */
export type StudySessionEndReason = "idle" | "inactive" | "suspended";

/** A closed interval; `end` is its last saved end, so `end - start` matches its saved row. */
export type StudySessionEnd = {
  end: number;
  id: string;
  reason: StudySessionEndReason;
  start: number;
};

/** No open-ended rows: a killed process can lose at most its last checkpoint interval. */
export function createStudySessionClock({
  createId,
  now = Date.now,
  onSessionEnd,
  onSessionStart,
  save,
}: {
  createId: () => string;
  now?: () => number;
  onSessionEnd?: (session: StudySessionEnd) => void;
  onSessionStart?: (session: { id: string; start: number }) => void;
  save: (session: StudySessionCheckpoint) => void;
}) {
  let enabled = false;
  let lastActivity = 0;
  let lastTick = now();
  let session: { end: number; id: string; start: number } | null = null;

  function close(reason: StudySessionEndReason) {
    if (session === null) {
      return;
    }
    const { end, id, start } = session;
    session = null;
    onSessionEnd?.({ end, id, reason, start });
  }

  function checkpoint() {
    const time = now();
    // A suspended timer or clock adjustment must not turn sleep into study time.
    if (time < lastTick || time - lastTick > STUDY_CHECKPOINT_MS * 2) {
      close("suspended");
    }
    lastTick = time;
    if (session === null) {
      return;
    }
    const end = Math.min(time, lastActivity + STUDY_IDLE_MS);
    if (end > session.end) {
      session.end = end;
      save({
        endedAt: new Date(end).toISOString(),
        id: session.id,
        startedAt: new Date(session.start).toISOString(),
      });
    }
    if (time >= lastActivity + STUDY_IDLE_MS) {
      close("idle");
    }
  }

  function activity() {
    if (!enabled) {
      return;
    }
    // Keep ordinary touches cheap; only close an expired interval here.
    const time = now();
    if (
      time - lastActivity >= STUDY_IDLE_MS ||
      time - lastTick > STUDY_CHECKPOINT_MS * 2 ||
      time < lastTick
    ) {
      checkpoint();
    }
    lastActivity = time;
    if (session === null) {
      lastTick = time;
      session = { end: time, id: createId(), start: time };
      onSessionStart?.({ id: session.id, start: time });
    }
  }

  function setEnabled(value: boolean) {
    if (enabled === value) {
      return;
    }
    checkpoint();
    enabled = value;
    if (enabled) {
      activity();
    } else {
      close("inactive");
    }
  }

  return { activity, checkpoint, setEnabled };
}
